import Papa from 'papaparse/papaparse.js';

export interface PlaylistData {
  name: string;
  description: string;
}

export interface ParseResult {
  success: boolean;
  data?: PlaylistData[];
  error?: string;
  rowErrors?: Array<{
    row: number;
    field: string;
    value: any;
    message: string;
  }>;
}

export interface CsvParseOptions {
  maxRows?: number;
  encoding?: string;
}

/**
 * Parse and validate CSV file for playlist import
 * @param file - The CSV file to parse
 * @param options - Parsing options
 * @returns Promise<ParseResult>
 */
export const parseCsvFile = (
  file: File,
  options: CsvParseOptions = {}
): Promise<ParseResult> => {
  return new Promise((resolve) => {
    const { maxRows = 1000, encoding = 'UTF-8' } = options;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      resolve({
        success: false,
        error: 'File size must be less than 5MB'
      });
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      resolve({
        success: false,
        error: 'Please select a CSV file'
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: encoding,
      transformHeader: (header: string) => header.trim().toLowerCase(),
      complete: (results) => {
        try {
          const parseResult = validateCsvData(results.data, results.errors, maxRows);
          resolve(parseResult);
        } catch (error) {
          resolve({
            success: false,
            error: `Failed to process CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          error: `Failed to parse CSV file: ${error.message}`
        });
      }
    });
  });
};

/**
 * Validate parsed CSV data structure and content
 * @param data - Parsed CSV data
 * @param parseErrors - Papa Parse errors
 * @param maxRows - Maximum allowed rows
 * @returns ParseResult
 */
const validateCsvData = (
  data: any[],
  parseErrors: Papa.ParseError[],
  maxRows: number
): ParseResult => {
  const rowErrors: Array<{
    row: number;
    field: string;
    value: any;
    message: string;
  }> = [];

  // Check for parse errors
  if (parseErrors && parseErrors.length > 0) {
    const criticalErrors = parseErrors.filter(error => 
      error.type === 'Delimiter' || error.type === 'Quotes'
    );
    
    if (criticalErrors.length > 0) {
      return {
        success: false,
        error: `CSV format error: ${criticalErrors[0].message}`
      };
    }
  }

  // Validate data exists
  if (!data || data.length === 0) {
    return {
      success: false,
      error: 'CSV file is empty or contains no valid data'
    };
  }

  // Check row limit
  if (data.length > maxRows) {
    return {
      success: false,
      error: `Too many rows. Maximum allowed: ${maxRows}, found: ${data.length}`
    };
  }

  // Validate required columns exist
  const firstRow = data[0];
  const requiredFields = ['name', 'description'];
  const availableFields = Object.keys(firstRow);
  
  const missingFields = requiredFields.filter(field => 
    !availableFields.includes(field)
  );

  if (missingFields.length > 0) {
    return {
      success: false,
      error: `Missing required columns: ${missingFields.join(', ')}. Expected columns: name, description`
    };
  }

  // Validate each row
  const validatedData: PlaylistData[] = [];
  const duplicateNames = new Set<string>();
  const namesSeen = new Set<string>();

  data.forEach((row, index) => {
    const rowNumber = index + 1;
    const name = typeof row.name === 'string' ? row.name.trim() : '';
    const description = typeof row.description === 'string' ? row.description.trim() : '';

    // Validate name field
    if (!name) {
      rowErrors.push({
        row: rowNumber,
        field: 'name',
        value: row.name,
        message: 'Playlist name is required'
      });
      return;
    }

    if (name.length > 100) {
      rowErrors.push({
        row: rowNumber,
        field: 'name',
        value: name,
        message: 'Playlist name must be 100 characters or less'
      });
      return;
    }

    // Check for duplicate names
    const nameKey = name.toLowerCase();
    if (namesSeen.has(nameKey)) {
      duplicateNames.add(name);
      rowErrors.push({
        row: rowNumber,
        field: 'name',
        value: name,
        message: 'Duplicate playlist name found'
      });
      return;
    }
    namesSeen.add(nameKey);

    // Validate description field
    if (description.length > 300) {
      rowErrors.push({
        row: rowNumber,
        field: 'description',
        value: description,
        message: 'Playlist description must be 300 characters or less'
      });
      return;
    }

    // Add to validated data if no errors
    validatedData.push({
      name: name,
      description: description || '' // Description is optional
    });
  });

  // Return results
  if (rowErrors.length > 0) {
    const errorSummary = `Found ${rowErrors.length} validation error(s) in the CSV file`;
    const firstFewErrors = rowErrors.slice(0, 5);
    const errorDetails = firstFewErrors
      .map(err => `Row ${err.row}, ${err.field}: ${err.message}`)
      .join('\n');
    
    return {
      success: false,
      error: `${errorSummary}\n\n${errorDetails}${rowErrors.length > 5 ? '\n... and more errors' : ''}`,
      rowErrors
    };
  }

  if (validatedData.length === 0) {
    return {
      success: false,
      error: 'No valid playlist data found in CSV file'
    };
  }

  return {
    success: true,
    data: validatedData
  };
};

/**
 * Download a sample CSV file for user reference
 */
export const downloadSampleCsv = (): void => {
  const sampleData = [
    { name: 'My Chill Playlist', description: 'Relaxing songs for evening' },
    { name: 'Workout Mix', description: 'High energy songs for gym' },
    { name: 'Study Music', description: 'Instrumental focus music' },
    { name: 'Road Trip Tunes', description: 'Perfect songs for long drives' },
    { name: 'Coffee Shop Vibes', description: 'Mellow background music' }
  ];

  const csv = Papa.unparse(sampleData, {
    quotes: true,
    header: true
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', 'sample_playlists.csv');
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Validate file before parsing (for quick validation in UI)
 * @param file - File to validate
 * @returns Object with validation result
 */
export const validateCsvFile = (file: File): { valid: boolean; error?: string } => {
  // File type validation
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return {
      valid: false,
      error: 'Please select a CSV file'
    };
  }

  // File size validation (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    return {
      valid: false,
      error: 'File size must be less than 5MB'
    };
  }

  // Empty file validation
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty'
    };
  }

  return { valid: true };
};
